/**
 * The app's default daily limits, for the funnel: the plan screens, the Loops
 * "Your Kidney Plan is Ready" numbers and the account the funnel creates.
 *
 * A hand copy of the app's `services/kidney/nutrientLimits.ts` calculateNutrientLimits
 * and `constants/fluid-presets.ts` getFluidLimitByStage, for the conditions the funnel
 * can produce. The funnel build cannot import those files (they use the app's `@/`
 * alias and React Native assets). Pure, no imports.
 *
 * `__tests__/funnelLimits.parity.test.ts` (root jest) asserts that every funnel kidney
 * status × dialysis type × comorbidity gives the app's numbers. Change the app first,
 * then this copy; never the other way round.
 */

export interface AppNutrientLimits {
  sodium: number
  potassium: number
  phosphorus: number
  proteinPerKg: number
  proteinRange: string
}

/** The stored conditions that mean "on dialysis" (app `shared/dialysis.ts` DIALYSIS_CONDITIONS). */
const DIALYSIS_CONDITIONS = ['on_dialysis', 'dialysis', 'ckd_stage_5_dialysis']

/** App 4.47 M7b: peritoneal dialysis potassium (app PD_POTASSIUM_MG). */
const PD_POTASSIUM_MG = 3000

function isDialysisCondition(condition: string): boolean {
  return DIALYSIS_CONDITIONS.includes(condition)
}

/**
 * Map a funnel kidney status to the app's KidneyCondition.
 * Funnel: stage_1, stage_2, stage_3a … App: ckd_stage_1, ckd_stage_2, ckd_stage_3a …
 */
export function toAppCondition(funnelStatus: string | null | undefined): string {
  const mapping: Record<string, string> = {
    stage_1: 'ckd_stage_1',
    stage_2: 'ckd_stage_2',
    stage_3a: 'ckd_stage_3a',
    stage_3b: 'ckd_stage_3b',
    stage_4: 'ckd_stage_4',
    stage_5: 'ckd_stage_5',
    on_dialysis: 'dialysis',
    kidney_stones: 'kidney_stones',
    no_diagnosis: 'not_sure_prevention',
    not_sure: 'not_sure_prevention',
  }
  return mapping[funnelStatus || 'not_sure'] || 'not_sure_prevention'
}

/**
 * App calculateNutrientLimits. On a dialysis condition, `not_started` (Starting soon)
 * gets the Stage 5 row (4.46 F7a) and `peritoneal` gets potassium 3,000 mg (4.47 M7b).
 * High blood pressure and heart disease each take 300 mg off sodium (floor 1,000 mg).
 */
export function appNutrientLimits(
  condition: string,
  comorbidities: readonly string[] = [],
  dialysisType?: string | null,
): AppNutrientLimits {
  const startingSoon = isDialysisCondition(condition) && dialysisType === 'not_started'
  let limits: AppNutrientLimits

  switch (startingSoon ? 'ckd_stage_5' : condition) {
    case 'ckd_stage_1':
    case 'ckd_stage_2':
      limits = { sodium: 2300, potassium: 3000, phosphorus: 1000, proteinPerKg: 0.8, proteinRange: '0.8g/kg' }
      break
    case 'ckd_stage_3a':
    case 'ckd_stage_3b':
      limits = { sodium: 2000, potassium: 2500, phosphorus: 800, proteinPerKg: 0.7, proteinRange: '0.6-0.8g/kg' }
      break
    case 'ckd_stage_4':
    case 'ckd_stage_5':
      limits = { sodium: 1500, potassium: 2000, phosphorus: 700, proteinPerKg: 0.6, proteinRange: '0.6g/kg' }
      break
    case 'dialysis':
    case 'on_dialysis':
    case 'ckd_stage_5_dialysis':
      limits = { sodium: 2000, potassium: 2500, phosphorus: 1000, proteinPerKg: 1.1, proteinRange: '1.0-1.2g/kg' }
      break
    case 'kidney_stones':
    case 'not_sure_prevention':
    default:
      limits = { sodium: 2300, potassium: 4000, phosphorus: 1000, proteinPerKg: 0.8, proteinRange: '0.8g/kg' }
  }

  if (isDialysisCondition(condition) && dialysisType === 'peritoneal') {
    limits = { ...limits, potassium: PD_POTASSIUM_MG }
  }

  let sodium = limits.sodium
  if (comorbidities.includes('high_blood_pressure')) sodium -= 300
  if (comorbidities.includes('heart_disease')) sodium -= 300
  return { ...limits, sodium: Math.max(1000, sodium) }
}

/**
 * App getFluidLimitByStage, in ml. 0 = no default fluid limit (peritoneal dialysis,
 * 4.48). Starting soon gets the Stage 5 number.
 */
export function appFluidLimit(condition: string, dialysisType?: string | null): number {
  if (isDialysisCondition(condition) && dialysisType === 'not_started') return 1200
  if (isDialysisCondition(condition) && dialysisType === 'peritoneal') return 0
  switch (condition) {
    case 'dialysis':
    case 'on_dialysis':
    case 'ckd_stage_5_dialysis':
      return 1000
    case 'ckd_stage_5':
      return 1200
    case 'ckd_stage_4':
      return 1500
    case 'ckd_stage_3a':
    case 'ckd_stage_3b':
      return 2000
    case 'ckd_stage_1':
    case 'ckd_stage_2':
      return 2500
    case 'kidney_stones':
      return 3000
    default:
      return 2500
  }
}
